import React, { useRef, useState } from "react";
import { motion } from "motion/react";
import { getCookie } from "@/lib/cookies";
import { API_URL } from "@/env";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import useReportsStore from "@/hooks/useReports";
import { showCustomToast } from "../CustomToast";

const ConfirmDeleteReport = ({
  setShowConfirmDelete,
  reportId,
  onDeleteSuccess, // Add this prop
}: {
  setShowConfirmDelete: React.Dispatch<React.SetStateAction<boolean>>;
  reportId: string;
  onDeleteSuccess?: (deletedReportId: string) => void; // Add this
}) => {
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);
  const token = getCookie("token");
  const { setReports, reports } = useReportsStore();

  const handleRemoveWebsite = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/reports/${reportId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      // console.log({ data });

      if (data._id) {
        setShowConfirmDelete(false);
        setReports(reports.filter((report: any) => report._id !== data._id));
        onDeleteSuccess?.(data._id); // Call the callback
        showCustomToast("Report deleted", "check");
      } else {
        showCustomToast("Couldn't delete report", "exclamation");
      }
    } catch (err) {
      console.log(err);
      showCustomToast("Couldn't delete report", "exclamation");
    } finally {
      setLoading(false);
    }
  };

  useOutsideClick(modalRef, () => {
    if (!loading) {
      setShowConfirmDelete(false);
    }
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="onest-md bg-gray-50/[0.72] backdrop-blur-md z-[100000] fixed top-0 left-0 w-screen h-screen flex items-center justify-center"
    >
      <div
        ref={modalRef}
        className="p-6 rounded-[24px] bg-gray-200 max-w-[480px] w-full"
      >
        <h4 className="text-[20px] leading-[28px]">
          Are you sure you want to delete this report?
        </h4>
        <p className="text-[14px] leading-[20px] text-white/[0.32] mt-2">
          This action cannot be undone and will permanently delete the report.
        </p>
        <div className="flex gap-3 mt-[32px]">
          <button
            onClick={() => setShowConfirmDelete(false)}
            disabled={loading}
            className="flex-1 flex items-center justify-center text-[14px] leading-[20px] h-[44px] rounded-[14px] bg-white/[0.04] text-white hover:bg-white/[0.08] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleRemoveWebsite}
            disabled={loading}
            className="flex-1 flex items-center text-[14px] leading-[20px] justify-center h-[44px] rounded-[14px] text-red-700 bg-red-alpha/[0.08] hover:bg-red-alpha/[0.16] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Deleting..." : "Yes, I'm sure"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ConfirmDeleteReport;
